// src/components/CartSidebar.jsx

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes, FaPlus, FaMinus, FaTrashAlt } from "react-icons/fa";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { getImageUrl } from "../utils/imageUrl";

const CartSideBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Destructure the newly available state and functions
  const {
    cartItems,
    isSidebarOpen,
    toggleSidebar,
    updateItemQuantity,
    removeItemFromCart,
  } = useCart();

  // Calculate the cart subtotal
  const subtotal = cartItems.reduce(
    (acc, item) =>
      acc + (item.Product ? item.Product.price * item.quantity : 0),
    0,
  );

  // --- Cart Item Row Component ---
  const CartItemRow = ({ item }) => {
    // ✅ HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState(item.quantity.toString());

    const product = item.Product;
    // Safety check in case product data is missing
    if (!product) return null;

    const productName = product[`name_${t("language")}`] || product.name_en;

    const imageUrl =
      product.images && product.images[0]
        ? getImageUrl(product.images[0].image_url)
        : `https://placehold.co/100x100/E0BBE4/FFFFFF?text=No+Image`;

    // ✅ CHECK IF WE'VE REACHED MAX STOCK
    const isMaxStock = item.quantity >= product.stock_quantity;
    const isOutOfStock = product.stock_quantity === 0;

    // Handle when user clicks on quantity to edit
    const handleQuantityClick = () => {
      setIsEditing(true);
      setInputValue(item.quantity.toString());
    };

    // Handle input change
    const handleInputChange = (e) => {
      const value = e.target.value;
      // Allow only numbers
      if (/^\d*$/.test(value)) {
        setInputValue(value);
      }
    };

    // Handle when user finishes editing (blur or Enter key)
    const handleInputBlur = () => {
      let newQty = parseInt(inputValue, 10);

      // Validate quantity
      if (isNaN(newQty) || newQty < 1) {
        newQty = 1;
      } else if (newQty > product.stock_quantity) {
        newQty = product.stock_quantity;
        alert(
          t(
            "stockExceeded",
            `Only ${product.stock_quantity} items available in stock`,
          ),
        );
      }

      setInputValue(newQty.toString());
      setIsEditing(false);

      // Update if different from current
      if (newQty !== item.quantity) {
        updateItemQuantity(item.cart_item_id, newQty);
      }
    };

    // Handle Enter key press
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        handleInputBlur();
      } else if (e.key === "Escape") {
        setIsEditing(false);
        setInputValue(item.quantity.toString());
      }
    };

    return (
      <div className="flex justify-between items-start py-4 border-b">
        <div className="flex items-center space-x-3 w-3/5">
          <img
            src={imageUrl}
            alt={productName}
            className="w-16 h-16 object-cover rounded-md"
          />
          <div className="flex flex-col">
            <h4 className="font-semibold text-gray-800 text-sm">
              {productName}
            </h4>
            <p className="text-sm text-gray-500"> {product.price} TL</p>
            {/* ✅ SHOW STOCK WARNING */}
            {isMaxStock && (
              <p className="text-xs text-orange-500 mt-1">
                {t("maxStockReached", "Max stock reached")}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end space-y-2 w-2/5">
          {/* Quantity Selector */}
          <div className="flex items-center border border-gray-300 rounded-md">
            <button
              onClick={() =>
                updateItemQuantity(item.cart_item_id, item.quantity - 1)
              }
              className="p-2 text-[#916A80] hover:bg-gray-100 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={item.quantity <= 1}
            >
              <FaMinus className="w-3 h-3" />
            </button>

            {/* ✅ EDITABLE QUANTITY INPUT */}
            {isEditing ? (
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                autoFocus
                className="w-12 px-2 text-sm font-medium text-center border-0 focus:outline-none focus:ring-1 focus:ring-[#916A80]"
              />
            ) : (
              <span
                onClick={handleQuantityClick}
                className="px-3 text-sm font-medium cursor-pointer hover:bg-gray-50 py-1 rounded"
                title={t("clickToEdit", "Click to edit quantity")}
              >
                {item.quantity}
              </span>
            )}

            <button
              onClick={() =>
                updateItemQuantity(item.cart_item_id, item.quantity + 1)
              }
              className="p-2 text-[#916A80] hover:bg-gray-100 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isMaxStock || isOutOfStock}
              title={
                isMaxStock
                  ? t("maxStockReached", "Max stock reached")
                  : undefined
              }
            >
              <FaPlus className="w-3 h-3" />
            </button>
          </div>

          {/* Remove Button and Total */}
          <div className="flex justify-between items-center w-full">
            <button
              onClick={() => removeItemFromCart(item.cart_item_id)}
              className="text-red-500 hover:text-red-700 transition duration-150 text-xs flex items-center space-x-1"
              title={t("remove", "Remove")}
            >
              <FaTrashAlt className="w-3 h-3" />
            </button>
            <span className="font-bold text-gray-800 text-sm">
              {product.price * item.quantity} TL
            </span>
          </div>
        </div>
      </div>
    );
  };
  // ------------------------------------

  return (
    <>
      {/* 1. Overlay Backdrop (Click to close) */}
      <div
        className={`fixed top-0 right-0 inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300 
          ${isSidebarOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
        onClick={() => toggleSidebar(false)} // Pass false to explicitly close
      ></div>

      {/* 2. Cart Sidebar Drawer */}
      <div
        className={
          `fixed top-0 right-0 w-full md:w-[450px] h-full bg-white shadow-5xl z-[70] 
          transform transition-transform duration-500 ease-in-out
          ${isSidebarOpen ? "translate-x-0" : "translate-x-full"}` // This controls the slide effect
        }
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-white z-10">
            <h2 className="text-2xl font-bold text-[#83375B]">
              {t("yourCart", "Your Cart")} ({cartItems.length})
            </h2>
            <button
              onClick={() => toggleSidebar(false)}
              className="text-gray-500 hover:text-gray-800 transition"
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>

          {/* Cart Items List */}
          <div className="flex-grow overflow-y-auto px-5">
            {cartItems.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <p>{t("cartEmpty", "Your cart is empty.")}</p>
                <button
                  onClick={() => toggleSidebar(false)}
                  className="mt-4 text-[#AA7F96] hover:underline"
                >
                  {t("continueShopping", "Continue Shopping")}
                </button>
              </div>
            ) : (
              cartItems.map((item) => (
                <CartItemRow key={item.cart_item_id} item={item} />
              ))
            )}
          </div>

          {/* Footer/Summary */}
          <div
            className={`p-5 border-t bg-gray-50 ${
              cartItems.length === 0 ? "hidden" : ""
            }`}
          >
            <div className="flex justify-between font-semibold text-lg mb-4">
              <span>{t("subtotal", "Subtotal")}:</span>
              <span>{subtotal} TL</span>
            </div>

            <button
              className="w-full bg-[#AA7F96] hover:bg-[#916A80] text-white font-bold py-3 rounded-lg 
                         transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#AA7F96] focus:ring-opacity-50"
              onClick={() => {
                // This should typically navigate to a separate /checkout page
                navigate("/cart");
                toggleSidebar(false); // Close sidebar on navigation
              }}
            >
              {t("checkoutBtn", "Go to Cart")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartSideBar;
